import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { User, CreateUserRequest, UpdateUserRequest, Role, Company } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppDialogFormActions } from '../common/AppDialog';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';

interface UserFormProps {
  user?: User | null;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: Role.EMPLOYEE,
    companyId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isSuperAdmin = currentUser?.role === Role.SUPER_ADMIN;
  const isEditing = !!user;

  // Fetch companies for Super Admin
  useEffect(() => {
    const fetchCompanies = async () => {
      if (isSuperAdmin && !isEditing) {
        try {
          setLoadingCompanies(true);
          const companiesData = await apiClient.getCompanies();
          setCompanies(companiesData);
        } catch (error: unknown) {
          setSubmitError(errorMessage(error, 'Failed to load companies'));
        } finally {
          setLoadingCompanies(false);
        }
      }
    };

    fetchCompanies();
  }, [isSuperAdmin, isEditing]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't populate password for editing
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role || Role.EMPLOYEE,
        companyId: user.companyId || '',
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: Role.EMPLOYEE,
        companyId: '',
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [user]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange = (field: string) => (event: { target: { value: unknown } }) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = t('profile.usernameRequired');
    } else if (formData.username.length < 3) {
      newErrors.username = t('profile.usernameMinLength');
    } else if (formData.username.length > 50) {
      newErrors.username = t('profile.usernameMaxLength');
    }

    // Email is optional, but if provided, must be valid
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('profile.emailInvalid');
    }

    if (!isEditing && !formData.password.trim()) {
      newErrors.password = t('users.passwordRequired');
    } else if (!isEditing && formData.password.length < 6) {
      newErrors.password = t('profile.newPasswordMinLength');
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('profile.firstNameRequired');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('profile.lastNameRequired');
    }

    // Company is required for Super Admin when creating COMPANY_ADMIN or EMPLOYEE
    if (isSuperAdmin && !isEditing && (formData.role === Role.COMPANY_ADMIN || formData.role === Role.EMPLOYEE)) {
      if (!formData.companyId.trim()) {
        newErrors.companyId = t('users.companyRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: CreateUserRequest | UpdateUserRequest = {
        username: formData.username.trim(),
        ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
      };

      // Add password only for new users or if password is provided for existing users
      if (!isEditing || formData.password.trim()) {
        (submitData as CreateUserRequest | UpdateUserRequest).password = formData.password;
      }

      // Add companyId for Super Admin when creating users for companies
      if (isSuperAdmin && !isEditing && formData.companyId.trim()) {
        (submitData as CreateUserRequest).companyId = formData.companyId;
      }

      await onSubmit(submitData);
    } catch (error: unknown) {
      setSubmitError(errorMessage(error, t('users.saveError')));
    }
  };

  const availableRoles = isSuperAdmin 
    ? [Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE]
    : [Role.EMPLOYEE];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {submitError && (
        <Alert severity="error" sx={{ mx: { xs: 2.5, sm: 3.5 }, mt: 2.5 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 2.5, sm: 3.5 } }}>
        <Typography variant="body2" color="text.secondary">{t('users.subtitle')}</Typography>

        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5
          }}
        >
          <TextField
            fullWidth
            label={t('users.firstName')}
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('users.lastName')}
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('users.username')}
            name="username"
            value={formData.username}
            onChange={handleInputChange('username')}
            error={!!errors.username}
            helperText={errors.username}
            required
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('common.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            placeholder={t('common.optional')}
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={isEditing ? `${t('users.password')} (${t('common.optional')})` : t('users.password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!errors.password}
            helperText={errors.password || (isEditing ? t('users.keepPassword') : '')}
            required={!isEditing}
            disabled={isLoading}
            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={showPassword ? t('profile.hidePassword') : t('profile.showPassword')} onClick={() => setShowPassword(value => !value)} edge="end"><>{showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}</></IconButton></InputAdornment> }}
          />

          <FormControl fullWidth error={!!errors.role} disabled={isLoading}>
            <InputLabel>{t('users.role')}</InputLabel>
            <Select
              value={formData.role}
              label={t('users.role')}
              onChange={handleSelectChange('role')}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {t(`role.${role}`)}
                </MenuItem>
              ))}
            </Select>
            {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
          </FormControl>

          {/* Company selection for Super Admin when creating users */}
          {isSuperAdmin && !isEditing && (formData.role === Role.COMPANY_ADMIN || formData.role === Role.EMPLOYEE) && (
            <FormControl fullWidth error={!!errors.companyId} disabled={isLoading || loadingCompanies}>
              <InputLabel>{t('users.company')}</InputLabel>
              <Select
                value={formData.companyId}
                label={t('users.company')}
                onChange={handleSelectChange('companyId')}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              {loadingCompanies && <FormHelperText>{t('users.loadingCompanies')}</FormHelperText>}
              {errors.companyId && <FormHelperText>{errors.companyId}</FormHelperText>}
            </FormControl>
          )}
        </Box>

      </Box>
      <AppDialogFormActions edgeToEdge={false}>
        <Button type="button" variant="outlined" onClick={onCancel} disabled={isLoading} sx={{ minWidth: { sm: 120 } }}>{t('common.cancel')}</Button>
        <Button type="submit" variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} /> : null} sx={{ minWidth: { sm: 160 } }}>
          {isLoading ? t('users.saving') : (isEditing ? t('users.update') : t('users.create'))}
        </Button>
      </AppDialogFormActions>
    </Box>
  );
};

export default UserForm;
